import apiClient from './client';

const API_URL = '/retail-performance-tasks';

// Create a new performance task
export const createPerformanceTask = async (taskData) => {
  const response = await apiClient.post(API_URL, taskData);
  return response.data;
};

// Get all performance tasks (with filters)
export const getTasks = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      // Handle arrays by joining with commas
      if (Array.isArray(value)) {
        queryParams.append(key, value.join(','));
      } else {
        queryParams.append(key, value);
      }
    }
  });

  const response = await apiClient.get(`${API_URL}?${queryParams.toString()}`);
  return response.data;
};

// Alias for getTasks
export const getPerformanceTasks = getTasks;

// Get tasks assigned to current user
export const getMyTasks = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      // Handle arrays by joining with commas
      if (Array.isArray(value)) {
        queryParams.append(key, value.join(','));
      } else {
        queryParams.append(key, value);
      }
    }
  });

  const response = await apiClient.get(`${API_URL}/my-tasks?${queryParams.toString()}`);
  return response.data;
};

// Alias for getMyTasks
export const getMyPerformanceTasks = getMyTasks;

// Get task by ID
export const getTaskById = async (id) => {
  const response = await apiClient.get(`${API_URL}/${id}`);
  return response.data;
};

// Alias for getTaskById
export const getPerformanceTaskById = getTaskById;

// Get task statistics
export const getTaskStatistics = async (id) => {
  const response = await apiClient.get(`${API_URL}/${id}/statistics`);
  return response.data;
};

// Alias for getTaskStatistics
export const getPerformanceTaskStatistics = getTaskStatistics;

// Pause task
export const pauseTask = async (id, reason) => {
  const response = await apiClient.patch(`${API_URL}/${id}/pause`, { reason });
  return response.data;
};

// Alias for pauseTask
export const pausePerformanceTask = pauseTask;

// Resume task
export const resumeTask = async (id) => {
  const response = await apiClient.patch(`${API_URL}/${id}/resume`);
  return response.data;
};

// Alias for resumeTask
export const resumePerformanceTask = resumeTask;

// Extend task deadline
export const extendDeadline = async (id, newEndTime) => {
  const response = await apiClient.patch(`${API_URL}/${id}/extend`, { new_end_time: newEndTime });
  return response.data;
};

// Alias for extendDeadline
export const extendPerformanceTaskDeadline = extendDeadline;

// End task early
export const endTaskEarly = async (id, reason) => {
  const response = await apiClient.patch(`${API_URL}/${id}/end`, { reason });
  return response.data;
};

// Alias for endTaskEarly
export const endPerformanceTaskEarly = endTaskEarly;

// Reassign task
export const reassignTask = async (id, userIds) => {
  const response = await apiClient.patch(`${API_URL}/${id}/reassign`, { user_ids: userIds });
  return response.data;
};

// Alias for reassignTask
export const reassignPerformanceTask = reassignTask;

// Cancel task
export const cancelTask = async (id, reason) => {
  const response = await apiClient.patch(`${API_URL}/${id}/cancel`, { reason });
  return response.data;
};

// Alias for cancelTask
export const cancelPerformanceTask = cancelTask;

// Delete task
export const deleteTask = async (id) => {
  const response = await apiClient.delete(`${API_URL}/${id}`);
  return response.data;
};

// Alias for deleteTask
export const deletePerformanceTask = deleteTask;

// Get inactive tasks
export const getInactiveTasks = async (filters = {}) => {
  const queryParams = new URLSearchParams();
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== null && value !== undefined && value !== '') {
      // Handle arrays by joining with commas
      if (Array.isArray(value)) {
        queryParams.append(key, value.join(','));
      } else {
        queryParams.append(key, value);
      }
    }
  });

  const response = await apiClient.get(`${API_URL}/inactive?${queryParams.toString()}`);
  return response.data;
};

// Alias for getInactiveTasks
export const getInactivePerformanceTasks = getInactiveTasks;

// Helper functions for UI consistency
export const getTaskStatusColor = (status) => {
  switch (status) {
    case 'active': return '#059669'; // Emerald-600
    case 'paused': return '#F59E0B'; // Amber-500
    case 'scheduled': return '#3B82F6'; // Blue-500
    case 'completed': return '#10B981'; // Green-500
    case 'failed': return '#EF4444'; // Red-500
    case 'ended': return '#6B7280'; // Gray-500
    case 'cancelled': return '#9CA3AF'; // Gray-400
    default: return '#6B7280';
  }
};

export const getTaskTypeLabel = (type) => {
  switch (type) {
    case 'sales_count': return 'Sales Count';
    case 'sales_amount': return 'Sales Revenue';
    case 'tiles_quantity': return 'Tiles Quantity';
    case 'accessories_quantity': return 'Accessories Quantity';
    default: return type;
  }
};

export const calculateProgressPercentage = (current, target) => {
  if (!target || target === 0) return 0;
  const percentage = (current / target) * 100;
  return Math.min(Math.max(parseFloat(percentage.toFixed(1)), 0), 100);
};
